import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ClockIcon, CurrencyDollarIcon, UserGroupIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import { api } from '../services/api';
import toast from 'react-hot-toast';

interface ProcessDetail {
  id: string;
  name: string;
  slug: string;
  category?: string;
  description?: string;
  overview?: string;
  detailed_steps?: any[];
  complexity_level?: string;
  automation_level?: string;
  precision_required?: string;
  typical_duration_hours?: number;
  setup_time_hours?: number;
  total_time_hours?: number;
  cost_per_unit?: number;
  cost_currency: string;
  equipment_required?: any[];
  equipment_cost?: number;
  facility_requirements?: any[];
  workers_required?: number;
  skill_level_required?: string;
  training_time_hours?: number;
  energy_consumption_kwh?: number;
  water_usage_liters?: number;
  waste_generated_kg?: number;
  carbon_footprint_kg?: number;
  safety_requirements?: any[];
  regulatory_compliance?: any[];
  hazard_level?: string;
  quality_checks?: any[];
  defect_rate_percentage?: number;
  yield_percentage?: number;
  image_url?: string;
  additional_images?: string[];
  video_url?: string;
  meta_title?: string;
  meta_description?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

interface ProcessComponent {
  id: string;
  name: string;
  slug: string;
  category?: string;
  product_name?: string;
  product_slug?: string;
  order_sequence?: number;
  is_primary_process: boolean;
  process_duration_hours?: number;
  process_cost?: number;
  yield_percentage?: number;
  defect_rate_percentage?: number;
}

const ProcessDetail = () => {
  const { processSlug } = useParams<{ processSlug: string }>();
  const navigate = useNavigate();
  const [process, setProcess] = useState<ProcessDetail | null>(null);
  const [components, setComponents] = useState<ProcessComponent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load process data
  const loadProcess = async (showRefreshToast = false) => {
    if (!processSlug) {
      setError('Process not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Load process details and components in parallel
      const [processData, componentsData] = await Promise.all([
        api.getProcessBySlug(processSlug),
        api.getProcessComponents(processSlug)
      ]);
      
      setProcess(processData);
      setComponents(componentsData);
      
      if (showRefreshToast) {
        toast.success('Process data refreshed');
      }
    } catch (err) {
      console.error('Failed to load process:', err);
      setError('Failed to load process data');
      if (!showRefreshToast) {
        toast.error('Failed to load process data');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadProcess();
  }, [processSlug]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadProcess(true);
  };

  const getComplexityColor = (level?: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'very_high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAutomationIcon = (level?: string) => {
    switch (level) {
      case 'manual': return '👷';
      case 'semi_automated': return '🤖';
      case 'automated': return '🏭';
      case 'fully_automated': return '⚡';
      default: return '🔧';
    }
  };

  const formatDuration = (hours?: number) => {
    if (!hours) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${hours} hours`;
    return `${Math.round(hours / 24)} days`;
  };

  // Loading state
  if (isLoading && !isRefreshing) {
    return (
      <>
        <title>Loading Process - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Loading process details...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !process) {
    return (
      <>
        <title>Process Not Found - ProductTrace</title>
        <div className="min-h-screen bg-gray-50">
          <Header />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🏭</div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Process Not Found</h1>
              <p className="text-gray-600 mb-6">{error || 'The manufacturing process you\'re looking for doesn\'t exist.'}</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{process.name} Process - ProductTrace</title>
      <meta name="description" content={process.meta_description || `Detailed information about ${process.name} manufacturing process`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button and Refresh */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isRefreshing ? (
                <>
                  <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Process Header */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">
                🏭 {process.name}
              </h1>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getComplexityColor(process.complexity_level)}`}>
                  {process.complexity_level || 'Unknown'} complexity
                </span>
                <span className="text-2xl">{getAutomationIcon(process.automation_level)}</span>
              </div>
            </div>
            
            {process.overview && (
              <p className="text-lg text-gray-600 mb-6">{process.overview}</p>
            )}

            {/* Process Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <ClockIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{formatDuration(process.typical_duration_hours)}</div>
                <div className="text-sm text-gray-600">Duration</div>
              </div>
              
              <div className="text-center">
                <CurrencyDollarIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">
                  {process.cost_per_unit ? `$${process.cost_per_unit}` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Cost per unit</div>
              </div>
              
              <div className="text-center">
                <UserGroupIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">{process.workers_required || 'N/A'}</div>
                <div className="text-sm text-gray-600">Workers needed</div>
              </div>
              
              <div className="text-center">
                <div className="w-8 h-8 text-orange-600 mx-auto mb-2 text-2xl">📊</div>
                <div className="text-2xl font-bold text-gray-900">
                  {process.yield_percentage ? `${process.yield_percentage}%` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Yield rate</div>
              </div>
            </div>
          </div>

          {/* Process Description */}
          {process.description && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Process Description</h2>
              <div className="prose max-w-none text-gray-700">
                {process.description.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-3">{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {/* Equipment Required */}
          {process.equipment_required && process.equipment_required.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">🔧 Equipment Required</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {process.equipment_required.map((equipment, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-900">{equipment.name || equipment}</div>
                    {equipment.cost && (
                      <div className="text-sm text-gray-600">${equipment.cost}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Safety Requirements */}
          {process.safety_requirements && process.safety_requirements.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                ⚠️ Safety Requirements
              </h2>
              <ul className="space-y-2">
                {process.safety_requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-yellow-600 mr-2">•</span>
                    <span className="text-gray-700">{requirement.description || requirement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Components Using This Process */}
          {components.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                🔗 Components Using This Process ({components.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {components.map((component) => (
                  <div
                    key={component.id}
                    className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                    onClick={() => navigate(`/components/${component.slug}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{component.name}</h3>
                      {component.is_primary_process && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Primary</span>
                      )}
                    </div>
                    {component.product_name && (
                      <div className="text-sm text-gray-600 mb-2">
                        Used in: {component.product_name}
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-500">
                      {component.process_duration_hours && (
                        <span>Duration: {formatDuration(component.process_duration_hours)}</span>
                      )}
                      {component.process_cost && (
                        <span>Cost: ${component.process_cost}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Environmental Impact */}
          {(process.energy_consumption_kwh || process.water_usage_liters || process.carbon_footprint_kg) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">🌱 Environmental Impact</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {process.energy_consumption_kwh && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-700">{process.energy_consumption_kwh} kWh</div>
                    <div className="text-sm text-green-600">Energy consumption</div>
                  </div>
                )}
                {process.water_usage_liters && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-700">{process.water_usage_liters} L</div>
                    <div className="text-sm text-blue-600">Water usage</div>
                  </div>
                )}
                {process.carbon_footprint_kg && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-700">{process.carbon_footprint_kg} kg CO₂</div>
                    <div className="text-sm text-gray-600">Carbon footprint</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProcessDetail; 